import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { User, Mail, Phone, Save, Loader2 } from "lucide-react";
import { PushNotificationButton } from "@/components/PushNotifications";

const API = '/api';

export default function Profile() {
  const { user, getAuthHeader, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    phone: ""
  });

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        phone: user.phone || ""
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(`${API}/auth/profile`, {
        email: formData.email.trim(),
        phone: formData.phone.trim() || null
      }, {
        headers: getAuthHeader()
      });
      
      // Refresh user data
      await checkAuth();
      
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      const message = error.response?.data?.detail || "Failed to update profile";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1B3B6F]">My Profile</h1>
        <p className="text-gray-600 mt-1">Manage your account settings</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User size={20} />
              Profile Information
            </CardTitle>
            <CardDescription>
              Update your email and phone number
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={user.name}
                  disabled
                  className="bg-gray-50"
                  data-testid="profile-name-input"
                />
                <p className="text-xs text-gray-500">Contact an admin to change your name</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-9"
                    placeholder="your@email.com"
                    data-testid="profile-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-9"
                    placeholder="(555) 123-4567"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                data-testid="profile-save-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Details Card */}
        <Card>
          <CardHeader>
            <CardTitle>Account Details</CardTitle>
            <CardDescription>
              Your account information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Role</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                user.role === "admin" 
                  ? "bg-amber-100 text-amber-800" 
                  : "bg-gray-100 text-gray-800"
              }`}>
                {user.role}
              </span>
            </div>

            {user.shooter_id && (
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-600">Shooter ID</span>
                <span className="font-mono font-medium">{user.shooter_id}</span>
              </div>
            )}

            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Status</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                user.status === "approved" 
                  ? "bg-green-100 text-green-800" 
                  : "bg-yellow-100 text-yellow-800"
              }`}>
                {user.status || "approved"}
              </span>
            </div>

            {/* Notification Settings */}
            <div className="pt-4">
              <h3 className="font-medium mb-3">Notification Settings</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Push Notifications</p>
                  <p className="text-xs text-gray-500">Receive score updates in real-time</p>
                </div>
                <PushNotificationButton />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
